(function () {
  "use strict";

  /**
   * Vendor Consent Bridge
   * =====================================================
   * This standalone script listens for the "SecuvyConsentStatus" event
   * emitted by consent widgets and propagates the decision to:
   *
   *  - Google Consent Mode (GA4 + Google Ads via GTM)
   *  - VWO (Visual Website Optimizer)
   *  - Facebook / other marketing tags via GTM dataLayer flags
   *  - Microsoft / Bing UET consent
   *  - Pinterest Tag consent
   *  - Shopify's customerPrivacy.setTrackingConsent (when available)
   *  - Klaviyo _learnq identify (marketing consent only)
   *
   * Include this file on any page that already loads the widget and
   * dispatches "SecuvyConsentStatus".
   *
   * Note: Pinterest Tag consent is applied using a queued approach to ensure
   * it works even if the Pinterest script is not loaded yet.
   */

  try {
    // ---------- Google Consent Mode DEFAULT (deny all) ----------
    window.dataLayer = window.dataLayer || [];
    // Ensure global gtag exists and re-use if GTM / GA snippet already added
    window.gtag =
      window.gtag ||
      function () {
        window.dataLayer.push(arguments);
      };

    window.gtag("consent", "default", {
      ad_storage: "denied",
      analytics_storage: "denied",
      ad_user_data: "denied",
      ad_personalization: "denied",
    });

    // ---------- Microsoft / Bing UET DEFAULT (deny ads storage) ----------
    try {
      window.uetq = window.uetq || [];
      // Default to denied until the  widget reports consent
      window.uetq.push("consent", "default", { ad_storage: "denied" });
    } catch (err) {
      // eslint-disable-next-line no-console
      console.warn("Unable to set default Bing consent:", err);
    }

    // ---------- Helper: map  categories to flags ----------
    const deriveFlags = categoryStatus => {
      // Adjust these mappings if your purposes use different names
      const analyticsAllowed = Boolean(categoryStatus.Analytics);
      const functionalAllowed = Boolean(categoryStatus.Functional);
      const marketingAllowed = Boolean(categoryStatus.Marketing || categoryStatus.Advertising);

      return { analyticsAllowed, functionalAllowed, marketingAllowed };
    };

    // ---------- Vendor-specific updaters ----------
    const applyGoogleConsent = (analyticsAllowed, marketingAllowed) => {
      if (typeof window.gtag !== "function") return;

      window.gtag("consent", "update", {
        analytics_storage: analyticsAllowed ? "granted" : "denied",
        ad_storage: marketingAllowed ? "granted" : "denied",
        ad_user_data: marketingAllowed ? "granted" : "denied",
        ad_personalization: marketingAllowed ? "granted" : "denied",
      });
    };

    const applyVWOConsent = analyticsAllowed => {
      try {
        // Ensure VWO command queue exists (SmartCode will pick this up)
        window._vwo_q = window._vwo_q || [];

        if (analyticsAllowed) {
          // User allowed analytics: let VWO start tracking and set cookies
          // Map  "Analytics" consent to VWO's analytics/targeting purposes
          window._vwo_pc_custom = {
            a: 1, // Analytics / tracking
            t: 1, // Targeting / personalization
          };

          // Notify VWO that consent is granted
          window._vwo_q.push(["track.consent.granted"]);
          // eslint-disable-next-line no-console
          console.log("VWO tracking enabled (analyticsAllowed = true)");
        } else {
          // User denied analytics: keep VWO disabled and opt out visitor
          window._vwo_q.push(["track.consent.denied"]);
          // Optional: explicit opt-out, will disable tracking and clear VWO cookies
          window._vwo_q.push(["optOutVisitor"]);
          // eslint-disable-next-line no-console
          console.log("VWO tracking disabled (analyticsAllowed = false)");
        }
      } catch (err) {
        console.error("Error applying VWO consent:", err);
      }
    };

    const applyFacebookConsent = marketingAllowed => {
      // For GTM-based Meta Pixel / other marketing tags, we expose a
      // dataLayer flag that tag triggers can evaluate.
      try {
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "secuvy_facebook_consent_update",
          secuvy_facebook_marketing: marketingAllowed ? "granted" : "denied",
        });
      } catch (err) {
        console.error("Error applying Facebook consent:", err);
      }
    };

    const applyBingConsent = marketingAllowed => {
      // Microsoft Ads / Bing UET consent mode
      // Maps  "Marketing/Advertising" category to Bing's ad_storage signal
      try {
        window.uetq = window.uetq || [];
        window.uetq.push("consent", "update", {
          ad_storage: marketingAllowed ? "granted" : "denied",
        });
        // eslint-disable-next-line no-console
        console.log("Bing UET ad_storage consent:", marketingAllowed ? "granted" : "denied");
      } catch (err) {
        console.error("Error applying Bing consent:", err);
      }
    };

    const applyPinterestConsent = marketingAllowed => {
      try {
        // Ensure pintrk queue exists (safe even before script loads)
        window.pintrk =
          window.pintrk ||
          function () {
            window.pintrk.queue.push(arguments);
          };

        window.pintrk.queue = window.pintrk.queue || [];

        // Apply Pinterest consent (queued if script not loaded yet)
        window.pintrk("setconsent", Boolean(marketingAllowed));

        // eslint-disable-next-line no-console
        console.log("Pinterest consent queued:", marketingAllowed ? "granted" : "denied");
      } catch (err) {
        console.error("Error applying Pinterest consent:", err);
      }
    };

    const applyKlaviyoIdentify = marketingAllowed => {
      try {
        if (!marketingAllowed) {
          window.__zinusKlaviyoIdentified = false;
          return;
        }

        if (window.__zinusKlaviyoIdentified) return;

        const payloadEl = document.getElementById("zinus-klaviyo-identify-payload");
        if (!payloadEl) return;

        let email = "";
        try {
          email = JSON.parse((payloadEl.textContent || "").trim());
        } catch (parseErr) {
          return;
        }

        if (!email || typeof email !== "string") return;

        window._learnq = window._learnq || [];
        window._learnq.push(["identify", { "$email": email }]);
        window.__zinusKlaviyoIdentified = true;
      } catch (err) {
        console.error("Error applying Klaviyo identify:", err);
      }
    };

    const applyShopifyConsent = (analyticsAllowed, marketingAllowed, functionalAllowed) => {
      try {
        const shopify = window.Shopify;
        const customerPrivacy = shopify && shopify.customerPrivacy;

        if (!customerPrivacy || typeof customerPrivacy.setTrackingConsent !== "function") {
          return;
        }

        customerPrivacy.setTrackingConsent(
          {
            analytics: analyticsAllowed,
            marketing: marketingAllowed,
            preferences: functionalAllowed,
            sale_of_data: marketingAllowed,
          },
          () => {
            // Optional callback after Shopify applies consent
            // eslint-disable-next-line no-console
            console.log("Shopify tracking consent updated");
          },
        );
      } catch (err) {
        console.error("Error applying Shopify consent:", err);
      }
    };

    // ---------- Listen for consent result ----------
    window.addEventListener("SecuvyConsentStatus", ev => {
      // console.log("Received SecuvyConsentStatus event", ev);
      try {
        const categoryStatus = (ev.detail && ev.detail.categoryStatus) || {};
        const { analyticsAllowed, marketingAllowed, functionalAllowed } =
          deriveFlags(categoryStatus);

        // Expose flags globally so GTM / other scripts can read them directly
        window.__secuvyConsent = {
          analytics: analyticsAllowed,
          functional: functionalAllowed,
          marketing: marketingAllowed,
        };

        // a) Google Consent Mode (GA4 + Google Ads)
        applyGoogleConsent(analyticsAllowed, marketingAllowed);

        // b) VWO
        applyVWOConsent(analyticsAllowed);

        // c) Facebook / other marketing tags via GTM
        applyFacebookConsent(marketingAllowed);

        // d) Microsoft / Bing UET (ads consent)
        applyBingConsent(marketingAllowed);

        // e) Pinterest Tag (marketing consent)
        applyPinterestConsent(marketingAllowed);

        // f) Shopify native tracking consent (only on Shopify storefronts)
        applyShopifyConsent(analyticsAllowed, marketingAllowed, functionalAllowed);

        // g) Klaviyo identify — marketing consent only (no _learnq before CMP grant)
        applyKlaviyoIdentify(marketingAllowed);

        // h) General GTM event so GTManager / GTM can gate any additional tags
        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          event: "secuvy_consent_update",
          secuvy_analytics_consent: analyticsAllowed ? "granted" : "denied",
          secuvy_marketing_consent: marketingAllowed ? "granted" : "denied",
        });
      } catch (err) {
        console.error("Error handling SecuvyConsentStatus:", err);
      }
    });
  } catch (err) {
    console.error("Error initializing vendor consent bridge:", err);
  }
})();
